import React, { useEffect } from "react";
import { Button } from "react-bootstrap";
import { MessageDisplayType, ActionHandlerFunction } from "@preveil-api";
import {
    Account, useAppSelector, useAppDispatch, DirectoryEntity, useBulkWatch, DriveWatchedErrorMessages, canWatchEntry, Message, MessageToastTypes,
    MessageHandlerDisplayType, EntryItem, DriveUIActionTypes, DriveWatchedMessages, MessageAnchors, DriveLimits
} from "src/common";
import { Icon } from "src/components";
import { uiActions, driveActions } from "src/store";
import { RootState } from "src/store/configureStore";
import _ from "lodash";

type AllProps = {
    current_account: Account;
    selected: string[];
    disabled: boolean;
    current_directory: DirectoryEntity;
    handleAction: ActionHandlerFunction;
}

function BulkWatchComponent(props: AllProps) {
    const { current_account, disabled, selected, current_directory, handleAction } = props; //  is_loading, collection_info, handleAction
    const default_collection_id = useAppSelector((state: RootState) => state.drive.root_info?.collection_id);
    const { setBulkWatchState, reset: resetWatch, success, error, bulk_action_errors } = useBulkWatch(current_account);
    const dispatch = useAppDispatch();

    // Description: Handle success and errors from watching nodes
    useEffect(() => {
        !!success && handleSuccess();
        !!error && handlePageErrorMessage(DriveWatchedErrorMessages.error_watch_bulk_nodes, undefined, MessageHandlerDisplayType.logger);
    }, [success, error]);

    // Description:  send error to store


    // Description: Filter out entries that should not be watched (regular directories under default collection)
    function handleBulkWatch() {
        const entries = current_directory?.entries;
        if (selected.length > DriveLimits.DRIVE_BULK_WATCH_WARNING_LIMIT) {
            const message = DriveWatchedMessages.bulk_watch_warning.replace(MessageAnchors.item_count, selected.length.toString());
            const confirmation_dialog = new Message(message, MessageHandlerDisplayType.confirm, MessageToastTypes.primary,
                DriveWatchedMessages.bulk_watch_warning_title,
                {
                    label: "Yes",
                    data: true,
                    action: () => { handleBulkWatchComplete(entries); },
                },
                { label: "No" },
            );
            dispatch(uiActions.handleSetMessage(confirmation_dialog));
        } else {
            handleBulkWatchComplete(entries);
        }
    }

    // Description: Trigger bulk action after warning or without
    function handleBulkWatchComplete(entries: EntryItem[]) {
        if (!!selected && !!entries && !!default_collection_id) {
            const _cannot_watch: EntryItem[] = [];
            const _entries = _.filter(entries, _entry => {
                let is_watchable = false;
                if (selected.includes(_entry.id)) {
                    is_watchable = canWatchEntry(_entry, default_collection_id);
                    !is_watchable && _cannot_watch.push(_entry);
                }
                return is_watchable;
            });
            // NOTE: Pass a warning for the user to know that not all are going to be added to watch list
            _cannot_watch.length > 0 && dispatch(uiActions.handleSetMessage(new Message(DriveWatchedErrorMessages.warning_watch_bulk_nodes,
                MessageHandlerDisplayType.toastr, MessageToastTypes.warning)));
            if (!!_entries && _entries.length) {
                dispatch(driveActions.setLoading(true));
                setBulkWatchState(_entries);
            }
        } else {
            handlePageErrorMessage(DriveWatchedErrorMessages.error_watch_bulk_nodes, undefined, MessageHandlerDisplayType.toastr);
        }
    }

    // Description: handle component level errors
    function handlePageErrorMessage(message: string, stack?: any, display_type: MessageDisplayType = MessageHandlerDisplayType.logger) {
        dispatch(uiActions.handleRequestErrors(new Message(message, display_type), stack));
    }

    // Description: Handle Success after toggle - If bulk action and there are failures then pass a warning
    function handleSuccess() {
        const warning = !!bulk_action_errors && bulk_action_errors.length > 0;
        const msg = warning ? DriveWatchedErrorMessages.error_watch_bulk_anchor.replace(MessageAnchors.item_names, bulk_action_errors.join(", ")) :
            DriveWatchedMessages.success_setting_watch.replace(MessageAnchors.actionType, "added to");
        dispatch(uiActions.handleSetMessage(new Message(msg, MessageHandlerDisplayType.toastr, warning ? MessageToastTypes.warning : MessageToastTypes.success)));
        handleAction({ actionType: DriveUIActionTypes.Refresh });
        resetWatch();
    }

    return <Button variant="icon" size="sm"
        disabled={disabled}
        aria-label="Watch Selected Items"
        onClick={handleBulkWatch}
        data-tooltip-id="tb-tooltip"
        data-tooltip-content="Watch Selected Items">
        <Icon className="ficon-eye" />
    </Button>;
}

export default React.memo(BulkWatchComponent);
